import type { OrganizationBranding, ThemeColorPreset } from './types'
import type { OrganizationLogoSize } from './types'

export type ThemeCssVariables = {
  '--color-primary': string
  '--color-primary-light': string
  '--color-primary-dark': string
}

export const FALLBACK_THEME_COLOR_PRESET: ThemeColorPreset = {
  label: '赤',
  base: '#E60023',
  light: '#FF4444',
  dark: '#AD081B',
}

export const DEFAULT_ORGANIZATION_LOGO_SIZE: OrganizationLogoSize = 'standard'

export function getEffectiveOrganizationLogoSize(
  value: string | null | undefined,
): OrganizationLogoSize {
  return value === 'large' ? 'large' : DEFAULT_ORGANIZATION_LOGO_SIZE
}

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/

export function normalizeHexColor(value: string | null | undefined): `#${string}` | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!HEX_COLOR_RE.test(trimmed)) return null
  return trimmed.toUpperCase() as `#${string}`
}

export function hexToRgbChannels(value: string | null | undefined): string | null {
  const hex = normalizeHexColor(value)
  if (!hex) return null
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r} ${g} ${b}`
}

export function getEffectiveThemeColorPreset(
  branding: Pick<OrganizationBranding, 'themeColorPreset'> | null | undefined,
): ThemeColorPreset {
  const preset = branding?.themeColorPreset
  if (
    !preset ||
    !hexToRgbChannels(preset.base) ||
    !hexToRgbChannels(preset.light) ||
    !hexToRgbChannels(preset.dark)
  ) {
    return FALLBACK_THEME_COLOR_PRESET
  }
  return {
    label: preset.label,
    base: normalizeHexColor(preset.base) ?? FALLBACK_THEME_COLOR_PRESET.base,
    light: normalizeHexColor(preset.light) ?? FALLBACK_THEME_COLOR_PRESET.light,
    dark: normalizeHexColor(preset.dark) ?? FALLBACK_THEME_COLOR_PRESET.dark,
  }
}

export function getThemeCssVariables(
  branding: Pick<OrganizationBranding, 'themeColorPreset'> | null | undefined,
): ThemeCssVariables {
  const preset = getEffectiveThemeColorPreset(branding)
  return {
    '--color-primary': hexToRgbChannels(preset.base) ?? hexToRgbChannels(FALLBACK_THEME_COLOR_PRESET.base)!,
    '--color-primary-light': hexToRgbChannels(preset.light) ?? hexToRgbChannels(FALLBACK_THEME_COLOR_PRESET.light)!,
    '--color-primary-dark': hexToRgbChannels(preset.dark) ?? hexToRgbChannels(FALLBACK_THEME_COLOR_PRESET.dark)!,
  }
}

export function shouldUseThemeColorAsSurface(
  branding: Pick<OrganizationBranding, 'useThemeColorAsSurface'> | null | undefined,
): boolean {
  return branding?.useThemeColorAsSurface ?? true
}
